import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import PublicNavigation from '../../components/ui/PublicNavigation';
import UnifiedFooter from '../../components/ui/UnifiedFooter';
import ServiceCard from './components/ServiceCard';
import AboutUsSection from './components/AboutUsSection';
import TimelineSection from './components/TimelineSection';
import TeamSection from './components/TeamSection';
import CertificationsSection from './components/CertificationsSection';
import FinalCTA from './components/FinalCTA';
import { Link } from 'react-router-dom';

const ShippingInformation = () => {
  const services = [
    {
      icon: "Truck",
      title: "Collecte à domicile",
      description: "Nous récupérons vos colis directement chez vous en Île-de-France, pour la province, nous consulter.",
      features: [
        "Service disponible en Région parisienne et régions proches",
        "Rendez-vous flexible selon votre disponibilité",
        "Emballage professionnel sur demande"
      ]
    },
    {
      icon: "Ship",
      title: "Transport maritime",
      description: "Acheminement sécurisé par voie maritime depuis le port d\'Anvers vers Abidjan.",
      features: [
        "Conteneurs sécurisés et suivis",
        "Assurance tous risques incluse",
        "Départs réguliers chaque semaine"
      ]
    },
    {
      icon: "FileText",
      title: "Gestion douanière",
      description: "Nous gérons toutes les formalités douanières pour vous en Côte d'Ivoire.",
      features: [
        "Déclaration en douane complète",
        "Paiement des taxes facilité",
        "Suivi en temps réel du dédouanement"
      ]
    },
    {
      icon: "Home",
      title: "Livraison finale",
      description: "Livraison à domicile ou en point relais à Abidjan et dans toute la Côte d'Ivoire.",
      features: [
        "Livraison à l\'adresse de votre choix",
        "Notification avant livraison",
        "Service de déballage disponible"
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Qui sommes nous — ADT Express</title>
        <meta name="description" content="ADT Express : envoi de colis vers la Côte d'Ivoire. Transport maritime régulier Paris-Abidjan, barriques noires et bleues, cartons triple cannelure. Service de collecte en Île-de-France. Diaspora ivoirienne." />
        <meta name="keywords" content="envoi colis Côte d'Ivoire, tarif envoi Côte d'Ivoire, envoyer barrique Côte d'Ivoire, fût maritime Paris Abidjan, achat barrique vide, achat barrique noire, envoi conteneur Abidjan, barrique diaspora ivoirienne, transport maritime Abidjan" />
      </Helmet>
      <div className="min-h-screen bg-white">
        <PublicNavigation />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-0 pt-44">
          {/* Section 1: Hero About Us */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <AboutUsSection />
          </motion.section>

          {/* Section 2: Timeline - Notre Histoire */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-gray-50"
          >
            <TimelineSection />
          </motion.section>

          {/* Section 3: Services - Notre savoir-faire */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-white py-16 sm:py-20"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                  Notre savoir-faire
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                  Un service complet de bout en bout pour vos expéditions
                </p>
              </div>
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                {services?.map((service, index) => (
                  <motion.div key={index} variants={itemVariants}>
                    <ServiceCard {...service} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.section>

          {/* Section 4: Team - Notre équipe */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-white"
          >
            <TeamSection />
          </motion.section>

          {/* Section 5: Certifications - Nos engagements */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-gray-50"
          >
            <CertificationsSection />
          </motion.section>

          {/* SEO Internal Links Section */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="py-12">
            <div className="bg-gradient-to-br from-green-50 to-gray-50 rounded-2xl p-8 md:p-10 border border-green-100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Prêt à expédier vers la Côte d'Ivoire ?
              </h2>
              <div className="space-y-5 text-base md:text-lg text-gray-700 leading-relaxed">
                <p>
                  Maintenant que vous comprenez notre processus de <strong>transport maritime Paris Abidjan</strong>, 
                  découvrez nos{' '}
                  <Link 
                    to="/dedicated-pricing-page" 
                    className="text-green-700 font-semibold hover:text-green-800 underline decoration-2 underline-offset-4 hover:decoration-green-600 transition-all">
                    tarifs compétitifs pour l'envoi de colis et barriques vers la Côte d'Ivoire
                  </Link>
                  . Nous proposons des solutions adaptées à tous les volumes, du particulier au professionnel.
                </p>
                <p>
                  Vous êtes importateur, commerçant ou entreprise avec des besoins réguliers ? Explorez notre{' '}
                  <Link 
                    to="/professional-services-landing-page" 
                    className="text-green-700 font-semibold hover:text-green-800 underline decoration-2 underline-offset-4 hover:decoration-green-600 transition-all">
                    offre professionnelle dédiée au groupage maritime et au transport conteneur Abidjan
                  </Link>
                  , avec gestion documentaire complète, certificats EX1 et accompagnement personnalisé.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Section 6: Final CTA */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <FinalCTA />
          </motion.section>
        </div>

        <UnifiedFooter />
      </div>
    </>
  );
};

export default ShippingInformation;